import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Cluster, ClusterService, TanMax } from 'src/app/core/cluster.service';
import { DialogService } from 'src/app/core/dialog.service';
import { ErrorService, SplittypayError } from 'src/app/core/error.service';
import { NavbarStatus, NavigationItem, NavigationService } from 'src/app/core/navigation.service';
import { UtilsService } from 'src/app/core/utils.service';
import { LOCAL_NAVBAR_STATUS_KEY } from 'src/app/shared/constants/operationals';
import { NavigationItemLabel } from 'src/app/shared/constants/translations';
import { environment } from 'src/environments/environment';
import { ClusterVersionDialogComponent } from '../cluster-version-dialog/cluster-version-dialog.component';
import { ClusterDeleteTanMaxDialogComponent } from '../cluster-delete-tan-max-dialog/cluster-delete-tan-max-dialog.component';

@Component({
  selector: 'app-cluster-detail',
  templateUrl: './cluster-detail.component.html',
})
export class ClusterDetailComponent {
  navbar_status: NavbarStatus;
  current_nav_item?: NavigationItem = this.navigation_service.navigation_set.nav_items.get(
    NavigationItemLabel.settings,
  );
  cluster_id: string;
  cluster_code?: string;
  cluster_name?: string;
  current_or_future_clusters: Cluster[] = [];
  past_clusters: Cluster[] = [];
  active_tan_max?: TanMax;
  future_tan_max: TanMax[] = [];
  tan_max_skeleton_rows: number[] = [1, 2, 3, 4, 5];
  is_tan_max_running: boolean = true;
  is_list_running: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cluster_service: ClusterService,
    private dialog_service: DialogService,
    private error_service: ErrorService,
    private navigation_service: NavigationService,
    private utils_service: UtilsService,
  ) {
    this.cluster_id = this.route.snapshot.paramMap.get('cluster_id') as string;
    this.navbar_status = (localStorage.getItem(LOCAL_NAVBAR_STATUS_KEY) as NavbarStatus) || NavbarStatus.closed;
  }

  ngOnInit(): void {
    this.listClusters();
  }

  listClusters(): void {
    this.is_list_running = true;
    this.cluster_service.readCluster(this.cluster_id).subscribe({
      next: response => {
        const now = Date.now();
        const response_cluster: Cluster = response.data;
        // TODO: Remove this fallback when cluster data is available in dev/local environments.
        const clusters = response_cluster
          ? [response_cluster]
          : this.getMockClusters().filter(cluster => cluster.cluster_id === this.cluster_id);
        this.cluster_code = clusters[0]?.cluster_code;
        this.cluster_name = clusters[0]?.name;
        this.current_or_future_clusters = clusters.filter(cluster => cluster.valid_to >= now);
        this.past_clusters = clusters.filter(cluster => cluster.valid_to < now);
        this.listTanMax();
        this.is_list_running = false;
      },
      error: error => {
        const error_status = error?.status ?? error?.statusCode ?? error?.response?.status;
        // TODO: Remove this fallback when cluster data is available in dev/local environments.
        if (this.isMockEnvironment() && (error_status === 404 || error_status === 500)) {
          const now = Date.now();
          const clusters = this.getMockClusters().filter(cluster => cluster.cluster_id === this.cluster_id);
          this.cluster_name = clusters[0]?.name;
          this.current_or_future_clusters = clusters.filter(cluster => cluster.valid_to >= now);
          this.past_clusters = clusters.filter(cluster => cluster.valid_to < now);
          this.setMockTanMax();
          this.is_list_running = false;
          return;
        }

        const processed_error: SplittypayError = this.error_service.preProcessApiError(error);
        this.error_service.processApiError(processed_error.code, processed_error.message);
        this.is_list_running = false;
      },
    });
  }

  listTanMax(): void {
    this.is_tan_max_running = true;
    this.cluster_service.listTanMaxByCluster(this.cluster_id).subscribe({
      next: response => {
        this.setTanMaxVersions((response.data?.data || response.data || []) as TanMax[]);
      },
      error: error => {
        const error_status = error?.status ?? error?.statusCode ?? error?.response?.status;
        if (this.isMockEnvironment() && (error_status === 404 || error_status === 500)) {
          // TODO: Remove this fallback when TanMax data is available in dev/local environments.
          this.setMockTanMax();
          return;
        }

        const processed_error: SplittypayError = this.error_service.preProcessApiError(error);
        this.error_service.processApiError(processed_error.code, processed_error.message);
        this.is_tan_max_running = false;
      },
    });
  }

  onAddClick(): void {
    this.openVersionDialog();
  }

  onEditClick(cluster: Cluster): void {
    this.openVersionDialog(cluster);
  }

  onDeleteTanMaxClick(tan_max: TanMax): void {
    this.dialog_service
      .openDialog(ClusterDeleteTanMaxDialogComponent, { tan_max })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result?.success) this.listTanMax();
        if (dialog_result?.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onBackClick(): void {
    this.router.navigate(['/cluster']);
  }

  onNavbarStatusChange(navbar_status: NavbarStatus): void {
    this.navbar_status = navbar_status;
    localStorage.setItem(LOCAL_NAVBAR_STATUS_KEY, navbar_status);
  }

  getNavbarClass(): { [key: string]: boolean | undefined } {
    return this.utils_service.getNavbarClass(this.navbar_status);
  }

  getToolbarWidthClass(): string {
    return this.navbar_status == NavbarStatus.open ? 'sp-toolbar__open-width' : 'sp-toolbar__closed-width';
  }

  private isMockEnvironment(): boolean {
    return ['local', 'dev', 'development', 'aminolfi'].includes(environment.STAGE.toLowerCase());
  }

  private getMockClusters(): Cluster[] {
    const valid_from = Date.now();
    const valid_to = new Date(valid_from);
    valid_to.setFullYear(valid_to.getFullYear() + 1);

    return [
      {
        cluster_id: 'cls_mock_retail',
        cluster_code: 'MOCK_RETAIL',
        name: 'Mock Retail Cluster',
        valid_from,
        valid_to: valid_to.getTime(),
        configurations: [
          { steps_number: 3, min_amount: 10000, max_amount: 100000, goal_tan: 500, min_tan: 0, max_tan: 700 },
          { steps_number: 6, min_amount: 10000, max_amount: 100000, goal_tan: 650, min_tan: 0, max_tan: 850 },
        ],
      },
      {
        cluster_id: 'cls_mock_professional',
        cluster_code: 'MOCK_PROFESSIONAL',
        name: 'Mock Professional Cluster',
        valid_from,
        valid_to: valid_to.getTime(),
        configurations: [
          { steps_number: 12, min_amount: 25000, max_amount: 250000, goal_tan: 750, min_tan: 0, max_tan: 950 },
        ],
      },
    ];
  }

  private setTanMaxVersions(tan_max_versions: TanMax[]): void {
    const now = Date.now();
    const sorted_versions = tan_max_versions.sort((first, second) => second.valid_from - first.valid_from);
    this.active_tan_max = sorted_versions.find(version => version.valid_from <= now);
    this.future_tan_max = sorted_versions.filter(version => version.valid_from > now).reverse();
    this.is_tan_max_running = false;
  }

  private setMockTanMax(): void {
    const now = Date.now();
    const future_valid_from = new Date(now);
    future_valid_from.setMonth(future_valid_from.getMonth() + 1);
    this.setTanMaxVersions([
      {
        tan_max_id: 'tmx_mock_active',
        valid_from: now - 86400000,
        cluster_id: this.cluster_id,
        cluster_code: this.cluster_code || 'MOCK_CLUSTER',
        configurations: [
          { steps_number: 4, value: 700 },
          { steps_number: 6, value: 850 },
          { steps_number: 8, value: 900 },
          { steps_number: 10, value: 950 },
          { steps_number: 12, value: 1000 },
        ],
      },
      {
        tan_max_id: 'tmx_mock_future',
        valid_from: future_valid_from.getTime(),
        cluster_id: this.cluster_id,
        cluster_code: this.cluster_code || 'MOCK_CLUSTER',
        configurations: [
          { steps_number: 4, value: 650 },
          { steps_number: 6, value: 800 },
          { steps_number: 8, value: 850 },
          { steps_number: 10, value: 900 },
          { steps_number: 12, value: 950 },
        ],
      },
    ]);
  }

  private openVersionDialog(cluster?: Cluster): void {
    this.dialog_service
      .openDialog(ClusterVersionDialogComponent, {
        cluster,
        cluster_id: this.cluster_id,
        cluster_code: this.cluster_code,
        name: this.cluster_name,
      })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result?.success) this.listClusters();
        if (dialog_result?.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }
}
